import { useNavigate, Link, useLocation } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
  IconButton,
  InputAdornment,
  Container,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Header from "../components/header";
import signupPagePhoto from "../assets/signupPagePhoto.png";
import LoginOAuthButtons from "../components/LoginOAuthButtons";
import getApiBase from "../components/getApiBase";
import TestAccountShopClaimModal from "../components/TestAccountShopClaimModal";
import TestAccountShopInviteModal from "../components/TestAccountShopInviteModal";
import RoleSelectionDialog from "../components/RoleSelectionDialog";
import TurnstileWidget from "../components/TurnstileWidget";

const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const isAutomatedBrowser =
  typeof navigator !== "undefined" && Boolean(navigator.webdriver);

const Login = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [role, setRole] = useState("User");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showResendLink, setShowResendLink] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const location = useLocation();
  const [redirectMessage, setRedirectMessage] = useState("");

  // Check for role parameter in URL (from email verification)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const roleParam = searchParams.get("role");
    const fromVerify = searchParams.get("fromVerify");
    if (roleParam) {
      // Map "Owner" to "Mechanic" for login (both use the same login button)
      // "User" stays as "User" (Car Owner)
      const loginRole = roleParam === "Owner" ? "Mechanic" : roleParam;
      if (loginRole === "Mechanic" || loginRole === "User") {
        setRole(loginRole);
      }
    }
    // Bug #2 fix: When VerifyEmail couldn't determine role (conditional break: failed verification or ref null),
    // it redirects with ?fromVerify=1. We open the role picker so the user selects Mechanic vs Car Owner
    // instead of defaulting to Car Owner.
    if (fromVerify === "1" && !roleParam) {
      setRoleDialogOpen(true);
    }
  }, [location.search]);

  useEffect(() => {
    const msg = localStorage.getItem("authRedirectMessage");
    if (msg) {
      setRedirectMessage(msg);
      localStorage.removeItem("authRedirectMessage");
    }
  }, []);

  const resetTurnstile = () => {
    turnstileRef.current?.reset();
    // TASK-676: Playwright E2E flag ? do not clear the token after widget.reset() re-issues it
    if (
      typeof window !== "undefined" &&
      window.__AUTOSHOPS_E2E_TURNSTILE_BYPASS__
    ) {
      setTurnstileToken("E2E_BYPASS_TOKEN");
      return;
    }
    setTurnstileToken(null);
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    resetTurnstile();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loginLoading) return;
    setLoginLoading(true);
    setError("");
    setSuccess("");
    setShowResendLink(false);

    if (!email || !password) {
      setError("Please enter both email and password.");
      setOpenSnackbar(true);
      setLoginLoading(false);
      return;
    }

    if (turnstileSiteKey && !turnstileToken && !isAutomatedBrowser) {
      setError("Please complete the bot verification challenge.");
      setOpenSnackbar(true);
      setLoginLoading(false);
      return;
    }

    try {
      const response = await fetch(`${getApiBase()}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, role, turnstileToken }),
      });

      //console.log("? Response status:", response.status);
      const contentType = response.headers.get("content-type");
      let data = {};
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        throw new Error("Server did not return JSON. Possible 404 or HTML error.");
      }

      if (!response.ok) {
        const errorMessage = data.message || "Login failed.";
        setError(errorMessage);
        setOpenSnackbar(true);
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        
        // Check if error is due to unverified email
        // Backend sends requiresEmailVerification flag, or we check the message
        const lowerMessage = errorMessage.toLowerCase();
        const isEmailVerificationError = 
          data.requiresEmailVerification === true ||
          lowerMessage.includes("verify your email") || 
          lowerMessage.includes("email not verified") ||
          lowerMessage.includes("email verification required");
        
        if (isEmailVerificationError) {
          setShowResendLink(true);
        } else {
          setShowResendLink(false);
        }
        return;
      }

      // Store user data and token for both cookie and header auth
      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
      }
      if (data.token) {
        localStorage.setItem("token", data.token);
      }
      
      // Check if test account needs to claim/join a shop
      if (data.isTestAccount) {
        if (data.requiresShopClaim) {
          // Owner needs to claim a shop
          setShowClaimModal(true);
          return; // Don't redirect yet
        } else if (data.requiresShopInvite) {
          // Mechanic needs to join a shop
          setShowInviteModal(true);
          return; // Don't redirect yet
        }
      }
      
      setSuccess("Login successful!");
      setOpenSnackbar(true);

      setTimeout(() => {
        const redirectParam = new URLSearchParams(location.search).get("redirect");
        const redirectTo = redirectParam || location.state?.redirectFrom ||
          (["Mechanic", "Owner"].includes(data.user.role) ? "/mechanic-profile" : "/profile");
        navigate(redirectTo);
      }, 1500);
    } catch (err) {
      console.error("Login error:", err);
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      setError("Error logging in. Please try again.");
      setOpenSnackbar(true);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email || !role) {
      setError("Please enter your email address first.");
      setOpenSnackbar(true);
      return;
    }

    setResendingEmail(true);
    setError("");

    try {
      const response = await fetch(`${getApiBase()}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess("Verification email sent! Check your inbox and spam folder.");
        setOpenSnackbar(true);
        setShowResendLink(false);
      } else {
        setError(data.error || "Failed to resend verification email.");
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.error("Resend verification error:", err);
      setError("Error sending verification email. Please try again.");
      setOpenSnackbar(true);
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          backgroundImage: `url(${signupPagePhoto})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 6,
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              borderRadius: 6,
              p: 5,
              boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.15)",
              textAlign: "center",
            }}
          >
            <Typography 
            variant="h4" 
            align="center"
            sx={{
              fontsize:{ xs: "1.8rem", sm: "2.2rem" },
              fontWeight: 800,
              color: "#e63926",
              mb: 4,
              fontFamily: "inherit",
            }}>
              Welcome Back
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
                gap: 2,
                bgcolor: "#e3e9f1",
                borderRadius: 10,
                p: 1,
                width: "fit-content",
                mx: "auto",
              }}
            >
              <Box
                component="button"
                onClick={() => handleRoleChange("User")}
                style={{
                  border: "none",
                  background: role === "User" ? "#e63926" : "transparent",
                  color: role === "User" ? "#fff" : "#000",
                  borderRadius: 8,
                  padding: "6px 20px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: "1em"
                }}
              >
                Car Owner
              </Box>
              <Box
                component="button"
                onClick={() => handleRoleChange("Mechanic")}
                style={{
                  border: "none",
                  background: role === "Mechanic" ? "#1976d2" : "transparent",
                  color: role === "Mechanic" ? "#fff" : "#000",
                  borderRadius: 8,
                  padding: "6px 20px",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: "1em"
                }}
              >
                Autoshop
              </Box>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TurnstileWidget
                ref={turnstileRef}
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={
                  loginLoading || ((turnstileSiteKey && !isAutomatedBrowser) ? !turnstileToken : false)
                  
                }
                sx={{ py: 1.6, fontSize: "1rem", mb: 2, fontWeight: 600 }}
              >
                {loginLoading ? "Logging In..." : "Log In"}
              </Button>
            </Box>
              {/*}
              <Button align="center" color="primary" onClick={() => (navigate("/forgot-password"))} sx={{ borderRadius: 2 }}>
                Forgot Password?
              </Button> 
              */}

            <Button onClick={() => navigate("/forgot-password")} sx={{ textTransform: "none" }}>
              Forgot Password?
            </Button>

            {showResendLink && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Button 
                  onClick={handleResendVerification} 
                  disabled={resendingEmail}
                  variant="outlined"
                  color="primary"
                  fullWidth
                  sx={{ 
                    textTransform: "none", 
                    py: 1.2,
                    fontWeight: 600,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2
                    }
                  }}
                >
                  {resendingEmail ? "Sending..." : "Resend Verification Email"}
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 3 }}>or</Divider>

            <LoginOAuthButtons role={role} />

            <Typography variant="body2">
              Don't have an account?{' '}
              <span
                onClick={() => setRoleDialogOpen(true)}
                style={{ 
                  fontWeight: "bold", 
                  color: "#1976d2",
                  cursor: "pointer",
                  textDecoration: "underline"
                }}
              >
                Sign Up
              </span>
            </Typography>
          </Box>

          {/* Role Selection Dialog */}
          <RoleSelectionDialog 
            open={roleDialogOpen} 
            onClose={() => setRoleDialogOpen(false)} 
          />

          <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)}>
            <Alert onClose={() => setOpenSnackbar(false)} severity={error ? "error" : "success"} sx={{ width: "100%" }}>
              {error || success}
            </Alert>
          </Snackbar>
        </Container>
      </Box>

      {/* Test Account Shop Claim Modal (for Owners) */}
      <TestAccountShopClaimModal
        open={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        userEmail={email}
        onSuccess={async (shop) => {
          setShowClaimModal(false);
          setSuccess(`Successfully claimed ${shop.shop_name}!`);
          setOpenSnackbar(true);
          
          // Update user data in localStorage with shop_id
          try {
            const userData = JSON.parse(localStorage.getItem("user") || "{}");
            userData.shop_id = shop.id;
            localStorage.setItem("user", JSON.stringify(userData));
          } catch (err) {
            console.error("Error updating user data:", err);
          }
          
          // Redirect after a short delay
          setTimeout(() => {
            const redirectTo = location.state?.redirectFrom || "/mechanic-profile";
            navigate(redirectTo);
          }, 1500);
        }}
      />

      {/* Test Account Shop Invite Modal (for Mechanics) */}
      <TestAccountShopInviteModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        userEmail={email}
        onSuccess={async (shop) => {
          setShowInviteModal(false);
          setSuccess(`Successfully joined ${shop.shop_name}!`);
          setOpenSnackbar(true);
          
          // Update user data in localStorage with shop_id
          try {
            const userData = JSON.parse(localStorage.getItem("user") || "{}");
            userData.shop_id = shop.id;
            localStorage.setItem("user", JSON.stringify(userData));
          } catch (err) {
            console.error("Error updating user data:", err);
          }
          
          // Redirect after a short delay
          setTimeout(() => {
            const redirectTo = location.state?.redirectFrom || "/mechanic-profile";
            navigate(redirectTo);
          }, 1500);
        }}
      />
    </>
  );
};

export default Login;
